import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';
import { uploadToCloudinary } from '../utils/cloudinary';

const GuideManager = ({ setMessage }) => {
    const { apiRequest } = useAdmin();
    const [guides, setGuides] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingGuide, setEditingGuide] = useState(null);
    const [saving, setSaving] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [imageSource, setImageSource] = useState('url');

    const [formData, setFormData] = useState({
        name: '',
        designation: '',
        bio: '',
        image: '/assets/img/default-guide.jpg',
        socialLinks: {
            facebook: '',
            twitter: '',
            instagram: '',
            linkedin: ''
        },
        order: 0,
        isActive: true
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        loadGuides();
    }, []);

    const loadGuides = async () => {
        try {
            setLoading(true);
            const data = await apiRequest('/guides/admin/all');
            if (data.success) {
                setGuides(data.guides || []);
            }
        } catch (error) {
            console.error('Error loading guides:', error);
            setMessage({ type: 'error', text: 'Error loading guides: ' + error.message });
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setMessage({ type: 'error', text: 'Please select a valid image file (JPEG, PNG, WebP)' });
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setMessage({ type: 'error', text: 'Image size should be less than 5MB' });
            return;
        }

        try {
            setImageUploading(true);
            setMessage({ type: 'info', text: 'Uploading image to Cloudinary...' });

            const result = await uploadToCloudinary(file);

            setFormData(prev => ({
                ...prev,
                image: result.url
            }));

            setImageSource('upload');
            setMessage({ type: 'success', text: 'Image uploaded successfully!' });
        } catch (error) {
            console.error('Cloudinary upload error:', error);
            setMessage({ type: 'error', text: 'Error uploading image: ' + error.message });
        } finally {
            setImageUploading(false);
        }
    };

    const handleEdit = (guide) => {
        setEditingGuide(guide);
        setFormData({
            name: guide.name,
            designation: guide.designation,
            bio: guide.bio || '',
            image: guide.image || '/assets/img/default-guide.jpg',
            socialLinks: guide.socialLinks || {
                facebook: '',
                twitter: '',
                instagram: '',
                linkedin: ''
            },
            order: guide.order,
            isActive: guide.isActive
        });

        if (guide.image && guide.image.includes('cloudinary.com')) {
            setImageSource('upload');
        } else {
            setImageSource('url');
        }

        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            setErrors({ name: 'Guide name is required' });
            return;
        }
        if (!formData.designation.trim()) {
            setErrors({ designation: 'Designation is required' });
            return;
        }

        setSaving(true);
        setErrors({});

        try {
            if (editingGuide) {
                const response = await apiRequest(`/guides/${editingGuide._id}`, {
                    method: 'PUT',
                    body: formData
                });

                if (response.success) {
                    setMessage({ type: 'success', text: 'Guide updated successfully!' });
                    setGuides(prev => prev.map(g =>
                        g._id === editingGuide._id ? { ...g, ...formData } : g
                    ));
                }
            } else {
                const response = await apiRequest('/guides', {
                    method: 'POST',
                    body: formData
                });

                if (response.success) {
                    setMessage({ type: 'success', text: 'Guide created successfully!' });
                    setGuides(prev => [...prev, { _id: response.guide._id, ...formData }]);
                }
            }

            setShowForm(false);
            setEditingGuide(null);
            resetForm();
            setImageSource('url');

        } catch (error) {
            console.error('Save guide error:', error);
            setMessage({
                type: 'error',
                text: `Error ${editingGuide ? 'updating' : 'creating'} guide: ${error.message}`
            });
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            designation: '',
            bio: '',
            image: '/assets/img/default-guide.jpg',
            socialLinks: {
                facebook: '',
                twitter: '',
                instagram: '',
                linkedin: ''
            },
            order: guides.length + 1,
            isActive: true
        });
        setImageSource('url');
        setErrors({});
    };

    const handleDelete = async (guideId, guideName) => {
        if (window.confirm(`Are you sure you want to delete "${guideName}"?`)) {
            try {
                const response = await apiRequest(`/guides/${guideId}`, {
                    method: 'DELETE'
                });

                if (response.success) {
                    setMessage({ type: 'success', text: 'Guide deleted successfully!' });
                    setGuides(prev => prev.filter(g => g._id !== guideId));
                }
            } catch (error) {
                console.error('Delete guide error:', error);
                setMessage({ type: 'error', text: 'Error deleting guide: ' + error.message });
            }
        }
    };

    const handleToggleActive = async (guide) => {
        try {
            const newStatus = !guide.isActive;
            const response = await apiRequest(`/guides/${guide._id}`, {
                method: 'PUT',
                body: { isActive: newStatus }
            });

            if (response.success) {
                setMessage({
                    type: 'success',
                    text: `Guide ${newStatus ? 'activated' : 'deactivated'} successfully!`
                });
                setGuides(prev => prev.map(g =>
                    g._id === guide._id ? { ...g, isActive: newStatus } : g
                ));
            }
        } catch (error) {
            console.error('Toggle guide error:', error);
            setMessage({ type: 'error', text: 'Error toggling guide: ' + error.message });
        }
    };

    const moveUp = async (index) => {
        if (index === 0) return;

        const updatedGuides = [...guides];
        const temp = updatedGuides[index];
        updatedGuides[index] = updatedGuides[index - 1];
        updatedGuides[index - 1] = temp;

        const reordered = updatedGuides.map((g, i) => ({ ...g, order: i + 1 }));
        setGuides(reordered);

        try {
            const orderedIds = reordered.map(g => g._id);
            await apiRequest('/guides/reorder', {
                method: 'POST',
                body: { orderedIds }
            });
            setMessage({ type: 'success', text: 'Order updated successfully!' });
        } catch (error) {
            console.error('Error reordering:', error);
            setMessage({ type: 'error', text: 'Error saving new order' });
            loadGuides();
        }
    };

    const moveDown = async (index) => {
        if (index === guides.length - 1) return;

        const updatedGuides = [...guides];
        const temp = updatedGuides[index];
        updatedGuides[index] = updatedGuides[index + 1];
        updatedGuides[index + 1] = temp;

        const reordered = updatedGuides.map((g, i) => ({ ...g, order: i + 1 }));
        setGuides(reordered);

        try {
            const orderedIds = reordered.map(g => g._id);
            await apiRequest('/guides/reorder', {
                method: 'POST',
                body: { orderedIds }
            });
            setMessage({ type: 'success', text: 'Order updated successfully!' });
        } catch (error) {
            console.error('Error reordering:', error);
            setMessage({ type: 'error', text: 'Error saving new order' });
            loadGuides();
        }
    };

    const removeImage = () => {
        setFormData(prev => ({
            ...prev,
            image: '/assets/img/default-guide.jpg'
        }));
        setImageSource('url');
    };

    if (loading && !guides.length) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading guides...</p>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                            <h4 className="mb-0">
                                <i className="fas fa-users me-2"></i>
                                Travel Teams Management
                            </h4>
                            <button
                                className="btn btn-light btn-sm"
                                onClick={() => {
                                    setEditingGuide(null);
                                    resetForm();
                                    setShowForm(true);
                                }}
                                disabled={loading}
                            >
                                <i className="fas fa-plus me-1"></i>
                                Add New Team Member
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row mb-4">
                <div className="col-md-4">
                    <div className="card bg-primary text-white">
                        <div className="card-body">
                            <div className="d-flex justify-content-between">
                                <div>
                                    <h4 className="mb-0">{guides.length}</h4>
                                    <small>Total Team Member</small>
                                </div>
                                <i className="fas fa-users fa-2x opacity-50"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card bg-success text-white">
                        <div className="card-body">
                            <div className="d-flex justify-content-between">
                                <div>
                                    <h4 className="mb-0">{guides.filter(g => g.isActive).length}</h4>
                                    <small>Active Team Member</small>
                                </div>
                                <i className="fas fa-check-circle fa-2x opacity-50"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card bg-warning text-dark">
                        <div className="card-body">
                            <div className="d-flex justify-content-between">
                                <div>
                                    <h4 className="mb-0">{guides.filter(g => !g.isActive).length}</h4>
                                    <small>Inactive Team Member</small>
                                </div>
                                <i className="fas fa-pause-circle fa-2x opacity-50"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-header bg-light">
                    <h6 className="mb-0">
                        <i className="fas fa-list me-2"></i>
                        Team Member List ({guides.length} items)
                    </h6>
                </div>
                <div className="card-body">
                    {guides.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="fas fa-users fa-3x text-muted mb-3"></i>
                            <h5 className="text-muted">No Team Member Found</h5>
                            <p className="text-muted">Get started by adding your first travel team member</p>
                            <button
                                className="btn btn-primary mt-3"
                                onClick={() => {
                                    setEditingGuide(null);
                                    resetForm();
                                    setShowForm(true);
                                }}
                            >
                                <i className="fas fa-plus me-1"></i>
                                Add First Team Member
                            </button>
                        </div>
                    ) : (
                        <div className="row">
                            {guides.map((guide, index) => (
                                <div key={guide._id} className="col-md-6 mb-3">
                                    <div className={`card ${!guide.isActive ? 'border-warning' : ''}`}>
                                        <div className="card-body">
                                            <div className="d-flex">
                                                <div className="flex-shrink-0 me-3">
                                                    <img
                                                        src={guide.image}
                                                        alt={guide.name}
                                                        className="rounded-circle"
                                                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                                        onError={(e) => {
                                                            e.target.src = '/assets/img/default-guide.jpg';
                                                        }}
                                                    />
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <h6 className="mb-1">{guide.name}</h6>
                                                            <p className="text-muted small mb-1">{guide.designation}</p>
                                                            {guide.bio && (
                                                                <p className="small text-muted mb-2">{guide.bio.substring(0, 80)}...</p>
                                                            )}
                                                        </div>
                                                        <span className={`badge ${guide.isActive ? 'bg-success' : 'bg-warning'}`}>
                                                            {guide.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex justify-content-between mt-2">
                                                        <div className="btn-group btn-group-sm">
                                                            <button
                                                                className="btn btn-outline-secondary btn-sm"
                                                                onClick={() => moveUp(index)}
                                                                disabled={index === 0}
                                                                title="Move Up"
                                                            >
                                                                <i className="fas fa-arrow-up"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-outline-secondary btn-sm"
                                                                onClick={() => moveDown(index)}
                                                                disabled={index === guides.length - 1}
                                                                title="Move Down"
                                                            >
                                                                <i className="fas fa-arrow-down"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-outline-primary btn-sm"
                                                                onClick={() => handleEdit(guide)}
                                                                title="Edit"
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </button>
                                                            <button
                                                                className={`btn btn-sm ${guide.isActive ? 'btn-warning' : 'btn-success'}`}
                                                                onClick={() => handleToggleActive(guide)}
                                                                title={guide.isActive ? 'Deactivate' : 'Activate'}
                                                            >
                                                                <i className={`fas ${guide.isActive ? 'fa-pause' : 'fa-play'}`}></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-outline-danger btn-sm"
                                                                onClick={() => handleDelete(guide._id, guide.name)}
                                                                title="Delete"
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {showForm && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">
                                    <i className="fas fa-users me-2"></i>
                                    {editingGuide ? 'Edit Guide' : 'Add New Guide'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setShowForm(false);
                                        setEditingGuide(null);
                                        resetForm();
                                    }}
                                    disabled={saving || imageUploading}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Team Member Name *</label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                required
                                                placeholder="e.g., Sarah Johnson"
                                                disabled={saving}
                                            />
                                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Designation *</label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.designation ? 'is-invalid' : ''}`}
                                                value={formData.designation}
                                                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                                                required
                                                placeholder="e.g., Manager"
                                                disabled={saving}
                                            />
                                            {errors.designation && <div className="invalid-feedback">{errors.designation}</div>}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Bio (Optional)</label>
                                        <textarea
                                            className="form-control"
                                            value={formData.bio}
                                            onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                                            rows="3"
                                            placeholder="Short description about the team member..."
                                            maxLength="500"
                                            disabled={saving}
                                        />
                                        <div className="form-text">Maximum 500 characters</div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Team Member Image</label>

                                        <div className="mb-3">
                                            <div className="form-check form-check-inline">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="imageSource"
                                                    id="imageSourceUrl"
                                                    value="url"
                                                    checked={imageSource === 'url'}
                                                    onChange={() => setImageSource('url')}
                                                    disabled={saving || imageUploading}
                                                />
                                                <label className="form-check-label" htmlFor="imageSourceUrl">Use Image URL</label>
                                            </div>
                                            <div className="form-check form-check-inline">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="imageSource"
                                                    id="imageSourceUpload"
                                                    value="upload"
                                                    checked={imageSource === 'upload'}
                                                    onChange={() => setImageSource('upload')}
                                                    disabled={saving || imageUploading}
                                                />
                                                <label className="form-check-label" htmlFor="imageSourceUpload">Upload Image</label>
                                            </div>
                                        </div>

                                        {imageSource === 'url' && (
                                            <div className="mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formData.image}
                                                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                                                    placeholder="/assets/img/guide-1.jpg or https://example.com/image.jpg"
                                                    disabled={saving || imageUploading}
                                                />
                                                <div className="form-text">
                                                    Enter image URL or path. Default: /assets/img/default-guide.jpg
                                                </div>
                                            </div>
                                        )}

                                        {imageSource === 'upload' && (
                                            <div className="mb-3">
                                                <div className="input-group">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleImageUpload}
                                                        className="form-control"
                                                        disabled={saving || imageUploading}
                                                    />
                                                </div>
                                                <div className="form-text">
                                                    Supported formats: JPG, PNG, WebP. Max size: 5MB
                                                </div>

                                                {imageUploading && (
                                                    <div className="alert alert-info mt-2 mb-0">
                                                        <div className="d-flex align-items-center">
                                                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                            <span>Uploading image to Cloudinary...</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {(formData.image && formData.image !== '/assets/img/default-guide.jpg') && (
                                            <div className="mt-3 p-3 border rounded bg-light">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <strong className="text-success">
                                                        <i className="fas fa-check-circle me-1"></i>
                                                        {imageSource === 'upload' ? 'Image Uploaded to Cloudinary' : 'Image URL Set'}
                                                    </strong>
                                                    <button
                                                        type="button"
                                                        onClick={removeImage}
                                                        className="btn btn-outline-danger btn-sm"
                                                        disabled={saving || imageUploading}
                                                    >
                                                        <i className="fas fa-times me-1"></i>
                                                        Remove Image
                                                    </button>
                                                </div>
                                                <div className="text-center">
                                                    <img
                                                        src={formData.image}
                                                        alt="Guide Preview"
                                                        className="img-fluid rounded shadow-sm"
                                                        style={{ maxHeight: '150px', objectFit: 'cover' }}
                                                        onError={(e) => e.target.src = '/assets/img/default-guide.jpg'}
                                                    />
                                                    <div className="mt-2">
                                                        <small className="text-muted">
                                                            {imageSource === 'upload' ? 'Hosted on Cloudinary CDN' : 'From provided URL'}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Social Links</label>
                                        <div className="row">
                                            <div className="col-md-6 mb-2">
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text"><i className="fab fa-facebook text-primary"></i></span>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Facebook URL"
                                                        value={formData.socialLinks.facebook}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            socialLinks: { ...formData.socialLinks, facebook: e.target.value }
                                                        })}
                                                        disabled={saving}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text"><i className="fab fa-twitter text-info"></i></span>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Twitter URL"
                                                        value={formData.socialLinks.twitter}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            socialLinks: { ...formData.socialLinks, twitter: e.target.value }
                                                        })}
                                                        disabled={saving}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text"><i className="fab fa-instagram text-danger"></i></span>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Instagram URL"
                                                        value={formData.socialLinks.instagram}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            socialLinks: { ...formData.socialLinks, instagram: e.target.value }
                                                        })}
                                                        disabled={saving}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text"><i className="fab fa-linkedin text-primary"></i></span>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="LinkedIn URL"
                                                        value={formData.socialLinks.linkedin}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            socialLinks: { ...formData.socialLinks, linkedin: e.target.value }
                                                        })}
                                                        disabled={saving}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Display Order</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={formData.order}
                                                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                                                min="0"
                                                disabled={saving}
                                            />
                                            <div className="form-text">Lower number appears first</div>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <div className="form-check form-switch mt-4">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={formData.isActive}
                                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                                    disabled={saving}
                                                />
                                                <label className="form-check-label">Active (Visible on website)</label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => {
                                                setShowForm(false);
                                                setEditingGuide(null);
                                                resetForm();
                                            }}
                                            disabled={saving || imageUploading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={saving || imageUploading}
                                        >
                                            {saving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    {editingGuide ? 'Updating...' : 'Creating...'}
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-save me-2"></i>
                                                        {editingGuide ? 'Update Team Member' : 'Create Team Member'}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GuideManager;